import { useRef, useMemo, useEffect, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useCursor } from "@react-three/drei";
import CustomShaderMaterial from "three-custom-shader-material";
import * as THREE from "three";
import { createNoise2D } from "simplex-noise";
import Particles from "./Particles";

function useNoiseTexture(width = 80, height = 80) {
    const texture = useMemo(() => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      const imageData = context.createImageData(width, height);
      const noise2D = createNoise2D();
  
      // The scale of the noise. Larger numbers = more zoomed in.
      const scale = 0.06; 
  
      for (let i = 0; i < imageData.data.length; i += 4) {
        const x = (i / 4) % width;
        const y = Math.floor((i / 4) / width);
  
        // Generate noise value in the range [-1, 1]
        const noise = noise2D(x * scale, y * scale);
  
        // Map noise value from [-1, 1] to [0, 255] for grayscale color
        const color = (noise + 1) * 227.5;
  
        imageData.data[i] = color;     // R
        imageData.data[i + 1] = color; // G
        imageData.data[i + 2] = color; // B
        imageData.data[i + 3] = 255;   // A
      }
  
      context.putImageData(imageData, 0, 0);
  
      const dataTexture = new THREE.CanvasTexture(canvas);
      dataTexture.wrapS = THREE.RepeatWrapping;
      dataTexture.wrapT = THREE.RepeatWrapping;
      dataTexture.needsUpdate = true;
  
      return dataTexture;
    }, [width, height]);
  
    return texture;
  }

// GLSL code for the vertex shader (NO CHANGES HERE)
const vertexShader = `
  vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
  vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
  vec3 fade(vec3 t) {return t*t*t*(t*(t*6.0-15.0)+10.0);}
  float cnoise(vec3 P){
    vec3 Pi0=floor(P);vec3 Pi1=Pi0+vec3(1.0);Pi0=mod(Pi0,289.0);Pi1=mod(Pi1,289.0);vec3 Pf0=fract(P);vec3 Pf1=Pf0-vec3(1.0);vec4 ix=vec4(Pi0.x,Pi1.x,Pi0.x,Pi1.x);vec4 iy=vec4(Pi0.yy,Pi1.yy);vec4 iz0=Pi0.zzzz;vec4 iz1=Pi1.zzzz;vec4 ixy=permute(permute(ix)+iy);vec4 ixy0=permute(ixy+iz0);vec4 ixy1=permute(ixy+iz1);vec4 gx0=ixy0/7.0;vec4 gy0=fract(floor(gx0)/7.0)-0.5;gx0=fract(gx0);vec4 gz0=vec4(0.5)-abs(gx0)-abs(gy0);vec4 sz0=step(gz0,vec4(0.0));gx0-=sz0*(step(0.0,gx0)-0.5);gy0-=sz0*(step(0.0,gy0)-0.5);vec4 gx1=ixy1/7.0;vec4 gy1=fract(floor(gx1)/7.0)-0.5;gx1=fract(gx1);vec4 gz1=vec4(0.5)-abs(gx1)-abs(gy1);vec4 sz1=step(gz1,vec4(0.0));gx1-=sz1*(step(0.0,gx1)-0.5);gy1-=sz1*(step(0.0,gy1)-0.5);vec3 g000=vec3(gx0.x,gy0.x,gz0.x);vec3 g100=vec3(gx0.y,gy0.y,gz0.y);vec3 g010=vec3(gx0.z,gy0.z,gz0.z);vec3 g110=vec3(gx0.w,gy0.w,gz0.w);vec3 g001=vec3(gx1.x,gy1.x,gz1.x);vec3 g101=vec3(gx1.y,gy1.y,gz1.y);vec3 g011=vec3(gx1.z,gy1.z,gz1.z);vec3 g111=vec3(gx1.w,gy1.w,gz1.w);vec4 norm0=taylorInvSqrt(vec4(dot(g000,g000),dot(g010,g010),dot(g100,g100),dot(g110,g110)));g000*=norm0.x;g010*=norm0.y;g100*=norm0.z;g110*=norm0.w;vec4 norm1=taylorInvSqrt(vec4(dot(g001,g001),dot(g011,g011),dot(g101,g101),dot(g111,g111)));g001*=norm1.x;g011*=norm1.y;g101*=norm1.z;g111*=norm1.w;float n000=dot(g000,Pf0);float n100=dot(g100,vec3(Pf1.x,Pf0.yz));float n010=dot(g010,vec3(Pf0.x,Pf1.y,Pf0.z));float n110=dot(g110,vec3(Pf1.xy,Pf0.z));float n001=dot(g001,vec3(Pf0.xy,Pf1.z));float n101=dot(g101,vec3(Pf1.x,Pf0.y,Pf1.z));float n011=dot(g011,vec3(Pf0.x,Pf1.yz));float n111=dot(g111,Pf1);vec3 fade_xyz=fade(Pf0);vec4 n_z=mix(vec4(n000,n100,n010,n110),vec4(n001,n101,n011,n111),fade_xyz.z);vec2 n_yz=mix(n_z.xy,n_z.zw,fade_xyz.y);float n_xyz=mix(n_yz.x,n_yz.y,fade_xyz.x);return 2.2*n_xyz;
  }

  uniform float u_time;
  uniform float u_strength;
  uniform float u_scale;
  uniform vec2 u_mouse;
  uniform float u_poke_strength;

  varying vec3 v_worldNormal;
  varying vec3 v_viewDirection;

  void main() {
    float noise = cnoise(position * u_scale + u_time * 0.5);
    vec3 baseDisplacement = normal * noise * u_strength;
    
    vec4 screenPosition = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    vec2 screenUV = screenPosition.xy / screenPosition.w;
    float mouseDistance = distance(screenUV, u_mouse);
    float pokeRadius = 0.4;
    float falloff = smoothstep(pokeRadius, 0.0, mouseDistance);
    vec3 pokeDisplacement = -normal * falloff * u_poke_strength;
    
    vec3 displacedPosition = position + baseDisplacement + pokeDisplacement;
    
    vec4 worldPos = modelMatrix * vec4(displacedPosition, 1.0);
    v_worldNormal = normalize(mat3(modelMatrix) * normal);
    v_viewDirection = normalize(cameraPosition - worldPos.xyz);
    
    csm_Position = displacedPosition;
  }
`;

// GLSL code for the fragment shader
const fragmentShader = `
  uniform float u_fresnel_power;
  uniform vec3 u_fresnel_color;
  uniform float u_fresnel_intensity;

  varying vec3 v_worldNormal;
  varying vec3 v_viewDirection;

  void main() {
    float fresnel = 1.0 - dot(normalize(v_viewDirection), normalize(v_worldNormal));
    fresnel = pow(fresnel, u_fresnel_power);
    
    vec3 fresnelColor = u_fresnel_color * fresnel * u_fresnel_intensity;
    
    // Use '+=' to ADD the rim light to the material's own emission.
    // This makes it a subtle effect instead of overwriting the base color.
    csm_Emissive += fresnelColor;
  }
`;


const BubbleCSM = ({ onExplosion, lowQuality = false }) => {
  const materialRef = useRef();
  const meshRef = useRef();
  const { width, height } = useThree((state) => state.viewport);
  const isHoveringRef = useRef(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isExploded, setIsExploded] = useState(false);
  const [explosionPosition, setExplosionPosition] = useState([0, 0, 0]);
  const iridescenceTexture = useNoiseTexture();
  // Gyroscope state
  const deviceMouseRef = useRef(new THREE.Vector2(0, 0));
  const hasDeviceOrientationRef = useRef(false);
  const baselineOrientationRef = useRef({ beta: null, gamma: null });
  const bubbleRadius = useMemo(() => {
    const computedRadius = width / 8;
    const MIN_RADIUS = 10; // ensure a minimum bubble size
    return Math.max(computedRadius, MIN_RADIUS);
  }, [width]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const sphereGeometry = useMemo(() => {
    const segments = lowQuality ? 24 : 64;
    return new THREE.SphereGeometry(bubbleRadius, segments, segments);
  }, [bubbleRadius, lowQuality]);

  // Enable device orientation on supported devices (mobile only). Baseline initial reading to zero.
  useEffect(() => {
    let cleanupFns = [];

    if (!isMobile) {
      hasDeviceOrientationRef.current = false;
      deviceMouseRef.current.set(0, 0);
      baselineOrientationRef.current = { beta: null, gamma: null };
      return () => {};
    }

    const handleOrientation = (event) => {
      const beta = typeof event.beta === 'number' ? event.beta : 0;   // front-back tilt
      const gamma = typeof event.gamma === 'number' ? event.gamma : 0; // left-right tilt

      if (baselineOrientationRef.current.beta === null || baselineOrientationRef.current.gamma === null) {
        baselineOrientationRef.current.beta = beta;
        baselineOrientationRef.current.gamma = gamma;
      }

      const deltaBeta = beta - baselineOrientationRef.current.beta;   // forward tilt is positive
      const deltaGamma = gamma - baselineOrientationRef.current.gamma; // right tilt is positive

      const maxTiltDeg = 30; // degrees mapped to full range
      const x = THREE.MathUtils.clamp(deltaGamma / maxTiltDeg, -1, 1);
      const y = THREE.MathUtils.clamp(-deltaBeta / maxTiltDeg, -1, 1); // invert so forward tilt moves up

      deviceMouseRef.current.set(x, y);
      hasDeviceOrientationRef.current = true;
    };

    const addOrientationListener = () => {
      window.addEventListener('deviceorientation', handleOrientation, { passive: true });
      cleanupFns.push(() => window.removeEventListener('deviceorientation', handleOrientation));
    };

    const tryEnable = async () => {
      try {
        // iOS permission gate
        if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
          const permission = await DeviceOrientationEvent.requestPermission();
          if (permission === 'granted') {
            addOrientationListener();
          }
        } else if (typeof window.DeviceOrientationEvent !== 'undefined') {
          // Android and others
          addOrientationListener();
        }
      } catch (_) {
        // ignore
      }
    };

    // On iOS require a user gesture; on others try immediately
    if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
      const onFirstInteraction = () => {
        tryEnable();
      };
      window.addEventListener('click', onFirstInteraction, { once: true });
      window.addEventListener('touchend', onFirstInteraction, { once: true });
      cleanupFns.push(() => window.removeEventListener('click', onFirstInteraction));
      cleanupFns.push(() => window.removeEventListener('touchend', onFirstInteraction));
    } else {
      tryEnable();
    }

    return () => {
      cleanupFns.forEach((fn) => fn());
      hasDeviceOrientationRef.current = false;
      deviceMouseRef.current.set(0, 0);
      baselineOrientationRef.current = { beta: null, gamma: null };
    };
  }, [isMobile]);

  // Change canvas cursor when hovering the bubble
  useCursor(isHovered);

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.uniforms.u_time.value = state.clock.elapsedTime;
      // Keep mouse-driven poke only for hover/pointer; gyroscope no longer drives poke
      materialRef.current.uniforms.u_mouse.value.copy(state.mouse);

      const targetStrength = isHoveringRef.current ? 1.0 : 0.0;
      let currentStrength = materialRef.current.uniforms.u_poke_strength.value;
      const lerpFactor = 0.08;
      currentStrength = THREE.MathUtils.lerp(currentStrength, targetStrength, lerpFactor);
      materialRef.current.uniforms.u_poke_strength.value = currentStrength;
    }

    // Float the whole bubble around using device orientation
    if (meshRef.current) {
      const padding = 0.5 * bubbleRadius; // keep some margin inside the viewport
      const xRange = Math.max(0, width / 2 - bubbleRadius - padding);
      const yRange = Math.max(0, height / 2 - bubbleRadius - padding);

      const targetX = hasDeviceOrientationRef.current ? deviceMouseRef.current.x * xRange : 0;
      const targetY = hasDeviceOrientationRef.current ? deviceMouseRef.current.y * yRange : 0;

      const positionLerp = 0.06;
      meshRef.current.position.x = THREE.MathUtils.lerp(meshRef.current.position.x, targetX, positionLerp);
      meshRef.current.position.y = THREE.MathUtils.lerp(meshRef.current.position.y, targetY, positionLerp);
    }
  });
  
  const uniforms = useMemo(() => ({
    // Vertex uniforms
    u_time: { value: 0 },
    u_strength: { value: lowQuality ? 0.18 : 0.3 },
    u_scale: { value: lowQuality ? 0.08 : 0.05 },
    u_mouse: { value: new THREE.Vector2(0, 0) },
    u_poke_strength: { value: 10 },
    
    // Fragment uniforms for Fresnel.
    // A higher power constricts the light to a thin rim.
    u_fresnel_power: { value: 100.0 }, 
    u_fresnel_color: { value: new THREE.Color('#000000FF') },
    // A lower intensity makes the rim light more subtle.
    u_fresnel_intensity: { value: 0 },
  }), [lowQuality]);

  const handleExplosion = () => {
    if (meshRef.current) {
      const worldPos = new THREE.Vector3();
      meshRef.current.getWorldPosition(worldPos);
      setExplosionPosition([worldPos.x, worldPos.y, worldPos.z]);
      if (onExplosion) {
        onExplosion(worldPos.clone());
      }
    }
    setIsExploded(true);
  };

  // Removed handleExplosionComplete - bubble stays exploded

  if (isExploded) {
    return <Particles radius={bubbleRadius} position={explosionPosition} />;
  }

  return (
    <mesh
      ref={meshRef}
      onPointerOver={() => {
        isHoveringRef.current = true;
        setIsHovered(true);
      }}
      onPointerOut={() => {
        isHoveringRef.current = false;
        setIsHovered(false);
      }}
      onClick={handleExplosion}
    >
      <primitive object={sphereGeometry} />
      <CustomShaderMaterial
        ref={materialRef}
        baseMaterial={THREE.MeshPhysicalMaterial}
        color="hsl(1, 0% 90%)"
        transmission={1}
        roughness={lowQuality ? 0.1 : 0}
        thickness={isMobile ? 0.25 : lowQuality ? 0.8 : 1.2}
        ior={1.33}
        iridescence={lowQuality ? 0.9 : 1.33}
        iridescenceIOR={1.33}
        iridescenceThicknessRange={[0, lowQuality ? 600 : 1000]}
        iridescenceThicknessMap={iridescenceTexture}
        clearcoat={0}
        clearcoatRoughness={1}
        attenuationDistance={Infinity}
        metalness={0}
        uniforms={uniforms}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        silent
      />
    </mesh>
  );
};

export default BubbleCSM;